/**
 * @fileoverview Deriva la lista de imágenes navegables del lightbox a partir de los mensajes del chat (inbox).
 * @module useInboxChatGalleryImages
 * @category Hook - UI Layer
 */
import { useMemo } from "react";

const componentTypeKey = "componentType";
const imageTypeValue = "ImageMessageMC";
const productTypeValue = "ProductWsMessageMC";
const mediaUrlKey = "contentMediaUrl";
const captionKey = "contentMediaCaption";

const carouselTypes = ["CarouselMessage", "CarouselMessageMC", "RepliedCarouselMessageMC"];
const repliedImageTypes = new Set([
  "RepliedImageMessageMC",
  "RepliedStickerMessageMC",
  "RepliedProductWsMessageMC"
]);

/**
 * @param {Array<Object>} messages - Mensajes de la conversación
 * @returns {Array<{ id: string|number, url: string, identifier?: string, caption?: string }>}
 */
function useInboxChatGalleryImages(messages = []) {
  return useMemo(() => {
    const normalizedMessages = messages || [];
    if (!normalizedMessages.length) return [];
    const hasValidUrl = (entry) => typeof entry?.url === "string" && entry.url.length > 0;

    const mapMsgToImageEntry = (msg) => ({
      id: msg.id,
      url: msg[mediaUrlKey],
      identifier: msg.identifier || undefined,
      caption: captionKey ? msg[captionKey] : undefined
    });

    const carouselImagesFromMessage = (msg) => {
      if (!msg || !carouselTypes.includes(msg?.[componentTypeKey])) return [];

      const headerType = (msg.cardHeaderType || msg.card_header_type || "IMAGE").toLowerCase();
      if (headerType !== "image") return [];

      const cards = msg.carouselCards || msg.carousel_cards || [];
      const baseId = msg.id || msg.identifier || msg.message_identifier;
      return cards
        .filter((card) => typeof card?.link === "string" && card.link.length > 0)
        .map((card, index) => ({
          id: `${baseId}-${index}`,
          url: card.link,
          caption: card.body || (captionKey ? msg[captionKey] : undefined)
        }));
    };

    const repliedNestedImages = normalizedMessages.flatMap((msg) => {
      const reply = msg.repliedMessage;
      if (!reply) return [];

      const replyType = reply[componentTypeKey] ?? reply.componentType;
      if (repliedImageTypes.has(replyType) && typeof reply[mediaUrlKey] === "string" && reply[mediaUrlKey].length > 0) {
        return [mapMsgToImageEntry(reply)];
      }
      return carouselImagesFromMessage(reply);
    });

    const imageMessages = normalizedMessages
      .filter((msg) => msg?.[componentTypeKey] === imageTypeValue || msg?.[componentTypeKey] === productTypeValue)
      .map(mapMsgToImageEntry);

    const carouselImageMessages = normalizedMessages.flatMap((msg) => carouselImagesFromMessage(msg));

    return [...imageMessages, ...carouselImageMessages, ...repliedNestedImages]
      .filter(hasValidUrl);
  }, [messages]);
}

export default useInboxChatGalleryImages;
